import { DayOfWeek, LocalDate } from "@js-joda/core";
import { useEffect, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import styled from "styled-components";
import { Api } from "services/api/api.service";
import { type GetTimeReportsV2SummaryResponse } from "services/api/company/timeReports.service";
import { formatIsoDate } from "web-apps/company/components/schedule/utils/schedule.dates";
import { formatSummaryDuration } from "web-apps/company/utils/timeAndDate";
import { getCostsTimeReports } from "web-apps/company/utils/utils";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { type Order } from "model/Analytics";
import { type TimeReportDto } from "model/TimeReport";
import { AnalyticsChart } from "./analytics.chart";

const CARD_ORDER_STORAGE_KEY = "analytics_card_order";

const DEFAULT_CARD_ORDER = [
  "sales",
  "personnelCost",
  "personnelCostPct",
  "plannedHours",
  "salesPerHour",
  "tips",
  "result",
];

const initCardOrder = (): string[] => {
  try {
    const stored = localStorage.getItem(CARD_ORDER_STORAGE_KEY);
    if (stored) {
      const parsed: string[] = JSON.parse(stored);
      const valid = parsed.filter((l) => DEFAULT_CARD_ORDER.includes(l));
      const newCards = DEFAULT_CARD_ORDER.filter((l) => !parsed.includes(l));
      return [...valid, ...newCards];
    }
  } catch {}
  return [...DEFAULT_CARD_ORDER];
};

type DeltaTone = "positive" | "negative" | "neutral";
type DeltaDirection = "up" | "down" | "neutral";

interface StatCardData {
  key: string;
  label: string;
  value: string;
  isLoading: boolean;
  delta: {
    display: string;
    direction: DeltaDirection;
    tone: DeltaTone;
  };
  forecast: string;
}

const DELTA_ICON: Record<DeltaDirection, string | null> = {
  up: "v2/up-chevron",
  down: "v2/down-chevron",
  neutral: null,
};

const DELTA_COLOR: Record<DeltaTone, ColorV2> = {
  positive: ColorV2.PositiveGreen,
  negative: ColorV2.NegativeRed,
  neutral: ColorV2.Blue4,
};

const formatCost = (amount: number): string =>
  `${Math.round(amount).toLocaleString("sv-SE")} kr`;

const getDateRange = (
  date: LocalDate,
  period: string,
): { fromDate: LocalDate; toDate: LocalDate } => {
  if (period === "week") {
    const weekStart = date.with(DayOfWeek.MONDAY);
    return { fromDate: weekStart, toDate: weekStart.plusDays(6) };
  }
  return { fromDate: date, toDate: date };
};

const buildPlannedHoursCard = (
  label: string,
  summary: GetTimeReportsV2SummaryResponse | null,
  isLoading: boolean,
): StatCardData => ({
  key: "plannedHours",
  label,
  value: summary ? formatSummaryDuration(summary.planned.time) : "–",
  isLoading,
  delta: { display: "–", direction: "neutral", tone: "neutral" },
  forecast: "–",
});

const buildPersonnelCostCard = (
  label: string,
  cost: number | null,
  isLoading: boolean,
): StatCardData => ({
  key: "personnelCost",
  label,
  value: cost !== null ? formatCost(cost) : "–",
  isLoading,
  delta: { display: "–", direction: "neutral", tone: "neutral" },
  forecast: "–",
});

const formatDateLabel = (
  date: LocalDate,
  period: string,
  todayLabel: string,
  yesterdayLabel: string,
): string => {
  if (period === "week") {
    const weekStart = date.with(DayOfWeek.MONDAY);
    const weekEnd = weekStart.plusDays(6);
    const fmt = (d: LocalDate) =>
      `${String(d.dayOfMonth()).padStart(2, "0")}/${String(d.monthValue()).padStart(2, "0")}`;
    return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
  }
  const today = LocalDate.now();
  if (date.isEqual(today)) return todayLabel;
  if (date.isEqual(today.minusDays(1))) return yesterdayLabel;
  return `${String(date.dayOfMonth()).padStart(2, "0")}/${String(date.monthValue()).padStart(2, "0")}`;
};

const toYearMonth = (date: LocalDate): number =>
  parseInt(`${date.year()}${String(date.monthValue()).padStart(2, "0")}`);


export const Analytics = () => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;

  const [period, setPeriod] = useState("day");
  const [selectedDate, setSelectedDate] = useState<LocalDate>(LocalDate.now());
  const [summary, setSummary] =
    useState<GetTimeReportsV2SummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [personnelCost, setPersonnelCost] = useState<number | null>(null);
  const [personnelCostLoading, setPersonnelCostLoading] = useState(false);
  const [sales, setSales] = useState<number | null>(null);
  const [tips, setTips] = useState<number | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [rawOrders, setRawOrders] = useState<Order[]>([]);
  const [rawTimeReports, setRawTimeReports] = useState<TimeReportDto[]>([]);
  const [cardOrder, setCardOrder] = useState<string[]>(initCardOrder);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropSlot, setDropSlot] = useState<number | null>(null);

  useEffect(() => {
    if (!companyId) return;
    setSummary(null);
    setSummaryLoading(true);
    const { fromDate, toDate } = getDateRange(selectedDate, period);
    Api()
      .company.timeReports.getTimeReportsV2Summary(
        companyId,
        formatIsoDate(fromDate),
        formatIsoDate(toDate),
      )
      .then((res) => {
        setSummary(res.data);
        setSummaryLoading(false);
      })
      .catch(() => {
        setSummary(null);
        setSummaryLoading(false);
      });
  }, [companyId, selectedDate, period]);

  useEffect(() => {
    if (!companyId) return;
    setPersonnelCost(null);
    setPersonnelCostLoading(true);
    setRawTimeReports([]);
    const { fromDate, toDate } = getDateRange(selectedDate, period);

    const yearMonths = new Set([toYearMonth(fromDate)]);
    if (
      fromDate.year() !== toDate.year() ||
      fromDate.monthValue() !== toDate.monthValue()
    ) {
      yearMonths.add(toYearMonth(toDate));
    }

    Promise.all(
      Array.from(yearMonths).map((ym) =>
        Api().company.timeReport.getAllTimeReports(companyId, ym),
      ),
    )
      .then(async (responses) => {
        const allReports = responses.flatMap((res) =>
          res.data.flatMap((w) => w.timeReports),
        );

        const filtered = allReports.filter((r) => {
          if (!r.startTime) return false;
          const reportDate = LocalDate.parse(r.startTime.substring(0, 10));
          return (
            !reportDate.isBefore(fromDate) && !reportDate.isAfter(toDate)
          );
        });

        setRawTimeReports(filtered);

        if (filtered.length === 0) {
          setPersonnelCost(0);
          setPersonnelCostLoading(false);
          return;
        }

        const costs = await getCostsTimeReports(filtered, companyId);
        setPersonnelCost(
          typeof costs?.totalCost === "number" ? costs.totalCost : null,
        );
        setPersonnelCostLoading(false);
      })
      .catch(() => {
        setRawTimeReports([]);
        setPersonnelCost(null);
        setPersonnelCostLoading(false);
      });
  }, [companyId, selectedDate, period]);

  useEffect(() => {
    if (!companyId) return;
    setSales(null);
    setTips(null);
    setRawOrders([]);
    setOrdersLoading(true);
    const { fromDate, toDate } = getDateRange(selectedDate, period);
    Api()
      .analytics.getOrders(companyId, fromDate.toString(), toDate.toString())
      .then((res) => {
        const orders = res.data;
        setRawOrders(orders);
        setSales(orders.reduce((sum, o) => sum + o.amount, 0));
        setTips(orders.reduce((sum, o) => sum + o.tip, 0));
        setOrdersLoading(false);
      })
      .catch(() => {
        setRawOrders([]);
        setSales(null);
        setTips(null);
        setOrdersLoading(false);
      });
  }, [companyId, selectedDate, period]);

  const personnelCostPct =
    personnelCost !== null && sales !== null && sales > 0
      ? (personnelCost / sales) * 100
      : null;

  const stats: StatCardData[] = [
    {
      key: "sales",
      label: t("CompanyAnalytics.Sales"),
      value: sales !== null ? formatCost(sales) : "–",
      isLoading: ordersLoading,
      delta: { display: "–", direction: "neutral", tone: "neutral" },
      forecast: "–",
    },
    buildPersonnelCostCard(t("CompanyAnalytics.PersonnelCost"), personnelCost, personnelCostLoading),
    {
      key: "personnelCostPct",
      label: t("CompanyAnalytics.PersonnelCostPct"),
      value: personnelCostPct !== null ? `${personnelCostPct.toFixed(1)}%` : "–",
      isLoading: ordersLoading || personnelCostLoading,
      delta: { display: "–", direction: "neutral", tone: "neutral" },
      forecast: "–",
    },
    buildPlannedHoursCard(t("CompanyAnalytics.PlannedHours"), summary, summaryLoading),
    {
      key: "salesPerHour",
      label: t("CompanyAnalytics.SalesPerHour"),
      value: "3 480 kr",
      isLoading: false,
      delta: { display: "+118 kr", direction: "up", tone: "positive" },
      forecast: "3 362 kr",
    },
    {
      key: "tips",
      label: t("CompanyAnalytics.Tips"),
      value: tips !== null ? formatCost(tips) : "–",
      isLoading: ordersLoading,
      delta: { display: "–", direction: "neutral", tone: "neutral" },
      forecast: "–",
    },
    {
      key: "result",
      label: t("CompanyAnalytics.Result"),
      value:
        sales !== null && personnelCost !== null
          ? formatCost(sales - personnelCost)
          : "–",
      isLoading: ordersLoading || personnelCostLoading,
      delta: { display: "–", direction: "neutral", tone: "neutral" },
      forecast: "–",
    },
  ];

  const orderedStats = [...stats].sort((a, b) => {
    const ai = cardOrder.indexOf(a.key);
    const bi = cardOrder.indexOf(b.key);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  const handleDragStart = (index: number) => setDragIndex(index);

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const slot = e.clientX < rect.left + rect.width / 2 ? index : index + 1;
    if (dropSlot !== slot) setDropSlot(slot);
  };

  const handleGridDragLeave = (e: React.DragEvent) => {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
      setDropSlot(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex === null || dropSlot === null) {
      setDragIndex(null);
      setDropSlot(null);
      return;
    }
    const newOrder = orderedStats.map((s) => s.key);
    const adjustedSlot = dragIndex < dropSlot ? dropSlot - 1 : dropSlot;
    const [moved] = newOrder.splice(dragIndex, 1);
    newOrder.splice(adjustedSlot, 0, moved);
    setCardOrder(newOrder);
    localStorage.setItem(CARD_ORDER_STORAGE_KEY, JSON.stringify(newOrder));
    setDragIndex(null);
    setDropSlot(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDropSlot(null);
  };

  const step = period === "week" ? 7 : 1;

  const periodOptions = [
    { value: "day", label: t("CompanyAnalytics.Day") },
    { value: "week", label: t("CompanyAnalytics.Week") },
  ];

  return (
    <Container>
      <Header>
        <Title>{t("CompanyAnalytics.Analytics")}</Title>
        <Controls>
          <SegmentController
            options={periodOptions}
            value={period}
            onChange={setPeriod}
          />
          <DateNav>
            <SecondaryButton
              iconName="v2/left-chevron"
              size="small"
              onClick={() => setSelectedDate((d) => d.minusDays(step))}
            />
            <DateLabel>
              {formatDateLabel(
                selectedDate,
                period,
                t("CompanyAnalytics.Today"),
                t("CompanyAnalytics.Yesterday"),
              )}
            </DateLabel>
            <SecondaryButton
              iconName="v2/right-chevron"
              size="small"
              onClick={() => setSelectedDate((d) => d.plusDays(step))}
            />
          </DateNav>
          <QuickButtons>
            <SecondaryButton
              label={t("CompanyAnalytics.Today")}
              size="small"
              onClick={() => setSelectedDate(LocalDate.now())}
            />
            <SecondaryButton
              label={t("CompanyAnalytics.Yesterday")}
              size="small"
              onClick={() => setSelectedDate(LocalDate.now().minusDays(1))}
            />
          </QuickButtons>
        </Controls>
      </Header>

      <CardsGrid onDragLeave={handleGridDragLeave} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
        {orderedStats.map((stat, index) => {
          const iconName = DELTA_ICON[stat.delta.direction];
          const color = DELTA_COLOR[stat.delta.tone];
          return (
            <StatCard
              key={stat.label}
              draggable
              $isDragging={dragIndex === index}
              $dropIndicator={
                dropSlot === index ? "left" : dropSlot === index + 1 ? "right" : null
              }
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
            >
              <DragHandle />
              <CardLabel>{stat.label}</CardLabel>
              {stat.isLoading ? (
                <CardLoader>
                  <CircularProgress size={24} />
                </CardLoader>
              ) : (
                <CardValue>{stat.value}</CardValue>
              )}
              {/* <DeltaRow $color={color}>
                {iconName ? (
                  <CustomIcon name={iconName} size="16px" color={color} />
                ) : (
                  <DeltaDash>—</DeltaDash>
                )}
                <DeltaValue>{stat.delta.display}</DeltaValue>
              </DeltaRow>
              <ForecastText>Prognos: {stat.forecast}</ForecastText> */}
            </StatCard>
          );
        })}
      </CardsGrid>
      <AnalyticsChart
        orders={rawOrders}
        timeReports={rawTimeReports}
        period={period}
        loading={ordersLoading || personnelCostLoading}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  ${TypographyV2.h5}
  color: ${ColorV2.Blue1};
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const DateNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DateLabel = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  min-width: 80px;
  text-align: center;
`;

const QuickButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardsGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
`;

const DragHandle = styled.div`
  position: absolute;
  top: 10px;
  right: 12px;
  width: 10px;
  height: 14px;
  background-image: radial-gradient(circle, ${ColorV2.Blue5} 1.5px, transparent 1.5px);
  background-size: 5px 5px;
  opacity: 0;
  transition: opacity 0.15s;
  pointer-events: none;
`;

const StatCard = styled.div<{
  $isDragging: boolean;
  $dropIndicator: "left" | "right" | null;
}>`
  min-width: 0;
  position: relative;
  background: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  border-radius: 16px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  opacity: ${({ $isDragging }) => ($isDragging ? 0.4 : 1)};
  cursor: grab;
  transition: opacity 0.15s;

  &:active {
    cursor: grabbing;
  }

  &:hover ${DragHandle} {
    opacity: 1;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 4px;
    bottom: 4px;
    width: 3px;
    border-radius: 2px;
    background: ${ColorV2.ElectricBlue};
    pointer-events: none;
  }

  &::before {
    left: -9px;
    opacity: ${({ $dropIndicator }) => ($dropIndicator === "left" ? 1 : 0)};
  }

  &::after {
    right: -9px;
    opacity: ${({ $dropIndicator }) => ($dropIndicator === "right" ? 1 : 0)};
  }
`;

const CardLabel = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;

const CardValue = styled.span`
  ${TypographyV2.h3}
  color: ${ColorV2.Blue1};
`;

const CardLoader = styled.div`
  display: flex;
  align-items: center;
  min-height: 36px;
`;

const DeltaRow = styled.div<{ $color: ColorV2 }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${(props) => props.$color};
`;

const DeltaDash = styled.span`
  ${TypographyV2.s1}
  line-height: 1;
`;

const DeltaValue = styled.span`
  ${TypographyV2.s1}
`;

const ForecastText = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;
